'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { DeleteConfirmationDialog } from '@/components/ui/delete-confirmation-dialog';
import { toast } from '@/lib/toast';
import { matterKeys, taskKeys } from '@/lib/query-keys';
import {
  deleteMatterAction,
  useMatterDeletionEligibility,
  type MatterDeletionEligibility
} from '@/lib/services/matters';
import { getPmsProviderLabel } from '@/lib/services/integrations/pms/provider-presentation';

interface DeleteMatterDialogProps {
  matterId: number;
  /** The matter as the firm names it, e.g. "Estate of the late Jane Doe". */
  matterTitle: string;
  reference?: string | null;
  isArchived: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Archive the matter; offered when it cannot be deleted. */
  onArchive: () => void;
  onDeleted?: () => void;
}

/**
 * Delete a matter the firm never made part of its record, or say why it can't (VALE-1927).
 *
 * The server owns the rule (`MATTER_FOOTPRINT_REGISTRY`) and refuses a delete with footprint
 * whichever client sends it; this asks it first so the user sees the reasons and archive
 * instead of a confirmation that would fail.
 */
export function DeleteMatterDialog({
  matterId,
  matterTitle,
  reference,
  isArchived,
  open,
  onOpenChange,
  onArchive,
  onDeleted
}: DeleteMatterDialogProps) {
  const queryClient = useQueryClient();
  const eligibility = useMatterDeletionEligibility(matterId, {
    enabled: open
  });

  useEffect(() => {
    if (open && eligibility.isError) {
      toast.error('Could not check whether this matter can be deleted');
      onOpenChange(false);
    }
  }, [open, eligibility.isError, onOpenChange]);

  if (!open || !eligibility.data) return null;

  const named = reference ? `${matterTitle} (${reference})` : matterTitle;

  if (!eligibility.data.deletable) {
    return (
      <CannotDeleteMatterDialog
        named={named}
        blockers={eligibility.data.blockers}
        isArchived={isArchived}
        onOpenChange={onOpenChange}
        onArchive={onArchive}
      />
    );
  }

  const handleDelete = async () => {
    const result = await deleteMatterAction(matterId);
    if (!result.success) {
      // Shown inside the dialog, e.g. when something was sent after it opened.
      queryClient.invalidateQueries({
        queryKey: matterKeys.deletionEligibility(matterId)
      });
      throw new Error(result.error || 'The matter could not be deleted');
    }
    queryClient.invalidateQueries({ queryKey: matterKeys.all });
    queryClient.invalidateQueries({ queryKey: taskKeys.all });
    toast.success('Matter deleted');
    onDeleted?.();
  };

  return (
    <DeleteConfirmationDialog
      open
      onOpenChange={onOpenChange}
      title="Delete this matter?"
      description={deleteDescription(named, eligibility.data)}
      itemName={named}
      destructiveWarning="Your firm cannot undo this."
      onConfirm={handleDelete}
    />
  );
}

function deleteDescription(
  named: string,
  eligibility: MatterDeletionEligibility
): string {
  const pms = getPmsProviderLabel(eligibility.pmsProvider);
  const base = `${named} and everything on it will be removed from Vale.`;
  return pms ? `${base} The matter in ${pms} is left untouched.` : base;
}

function CannotDeleteMatterDialog({
  named,
  blockers,
  isArchived,
  onOpenChange,
  onArchive
}: {
  named: string;
  blockers: MatterDeletionEligibility['blockers'];
  isArchived: boolean;
  onOpenChange: (open: boolean) => void;
  onArchive: () => void;
}) {
  return (
    <AlertDialog open onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>This matter can’t be deleted</AlertDialogTitle>
          <AlertDialogDescription>
            {named} is part of your firm’s record.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul
          className="text-muted-foreground list-disc space-y-1 pl-5 text-sm"
          data-testid="matter-deletion-blockers"
        >
          {blockers.map((b) => (
            <li key={b.code}>{b.label}</li>
          ))}
        </ul>
        {!isArchived && (
          <p className="text-muted-foreground text-sm">
            You can archive it instead.
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          {!isArchived && (
            <AlertDialogAction onClick={onArchive}>
              Archive matter
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
